// .pen: Selfie · Relatório · <estado> · <viewport> <tema> (wcY8Z e restantes
// 19 frames). O back+título "Relatório IA" (rrW5W) já vem do chrome global
// da app (mobileRouteHeader.js já regista esta rota — confirmado em QA no
// browser: um cabeçalho inline próprio duplicava back+título). Este ecrã só
// acrescenta as ações de topo que o header global não suporta (partilhar +
// imprimir), sem repetir back/título.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { paths } from '../../routes/paths';
import {
  ArrowClockwise,
  ArrowRight,
  FileText,
  Sparkle,
  ShareNetwork,
  WarningCircle,
} from '@phosphor-icons/react';

import { Button } from '../../components/ui/Button';
import { SelfieReportGenerating } from '../../components/snapshots/report/SelfieReportGenerating';
import { SelfieReportVerdictBubble } from '../../components/snapshots/report/SelfieReportVerdictBubble';
import { SelfieReportHighlights } from '../../components/snapshots/report/SelfieReportHighlights';
import { SelfieReportNextSteps } from '../../components/snapshots/report/SelfieReportNextSteps';
import { SelfieReportDetailsAccordion } from '../../components/snapshots/report/SelfieReportDetailsAccordion';
import { ReportMessage } from '../../components/snapshots/report/ReportPrimitives';
import { EmptyState } from '../../components/common/EmptyState';
import { SelfieEmergencyFundReviewModal } from '../../components/snapshots/SelfieEmergencyFundReviewModal';
import { SelfieMonthlyCheckModal } from '../../components/snapshots/SelfieMonthlyCheckModal';
import {
  useEmergencyFundReviewQuery,
  useSelfieReportQuery,
  useSnapshotsListQuery,
} from '../../features/snapshots/queries';
import { authAPI } from '../../data/auth';
import { useAuth } from '../../store/AuthContext';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import pt from '../../locales/pt';
import {
  fillReportPlaceholders,
  isReportPending,
  markReportSeen,
} from '../../utils/reportPlaceholders';
import { hasAiNarrative } from '../../lib/reportUiUtils';

function ReportTopBar({ onShare, canShare }) {
  return (
    <div className="mb-space-5 flex items-center justify-end gap-1 print:hidden">
      {canShare ? (
        <Button type="button" variant="ghost" size="sm" onClick={onShare} aria-label={pt.snapshots.report.share}>
          <ShareNetwork className="h-4 w-4" />
        </Button>
      ) : null}
      <Button type="button" variant="ghost" size="sm" onClick={() => window.print()}>
        <FileText className="h-4 w-4" />
        <span className="sr-only">{pt.snapshots.report.print}</span>
      </Button>
    </div>
  );
}

export function SelfieReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const onboarding = params.get('onboarding') === '1';
  const { profile, refreshMe } = useAuth();
  const [slowGeneration, setSlowGeneration] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [checkOpen, setCheckOpen] = useState(!onboarding);
  const [efReviewOpen, setEfReviewOpen] = useState(!onboarding);
  const { data: snapshots = [] } = useSnapshotsListQuery();
  const snapshot = snapshots.find((row) => row.id === id) || null;
  const { data: efReview } = useEmergencyFundReviewQuery(id, {
    enabled: Boolean(id) && !onboarding,
  });
  const {
    data: reportRaw,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useSelfieReportQuery(id);

  const report = useMemo(() => fillReportPlaceholders(reportRaw, profile), [reportRaw, profile]);
  const canShare = typeof navigator !== 'undefined' && Boolean(navigator.share);

  useEffect(() => {
    if (efReview && !efReview.show_review) {
      setEfReviewOpen(false);
    }
  }, [efReview]);

  useEffect(() => {
    if (report?.status === 'ready' && id) {
      markReportSeen(id);
    }
  }, [report?.status, id]);

  useEffect(() => {
    if (!isReportPending(report?.status)) {
      setSlowGeneration(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setSlowGeneration(true), 30000);
    return () => window.clearTimeout(timer);
  }, [report?.status]);

  const goToPatrimonio = async () => {
    if (onboarding) {
      setAdvancing(true);
      await authAPI.saveOnboardingStep('completed', true);
      await refreshMe();
      setAdvancing(false);
      navigate(paths.dashboard, { replace: true });
      return;
    }
    navigate(paths.patrimonio);
  };

  const handleShare = async () => {
    if (!canShare) return;
    try {
      await navigator.share({
        title: pt.snapshots.report.pageTitle.replace('{period}', report?.selfie?.period?.label || ''),
        url: window.location.href,
      });
    } catch {
      /* user dismissed */
    }
  };

  const postSelfieModals =
    !onboarding && snapshot ? (
      <>
        <SelfieEmergencyFundReviewModal
          snapshotId={snapshot.id}
          isOpen={efReviewOpen}
          onClose={() => setEfReviewOpen(false)}
        />
        <SelfieMonthlyCheckModal
          snapshotId={snapshot.id}
          year={snapshot.reference_year}
          month={snapshot.reference_month}
          isOpen={checkOpen && !efReviewOpen}
          onClose={() => setCheckOpen(false)}
        />
      </>
    ) : null;

  const patrimonioCta = (
    <div className="mt-space-8 flex justify-center print:hidden">
      <Button onClick={goToPatrimonio} disabled={advancing} className="w-full max-w-md">
        {advancing ? pt.common.loading : onboarding ? pt.snapshots.result.ctaDashboard : pt.snapshots.result.cta}{' '}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Button>
    </div>
  );

  if (isLoading && !report) {
    return (
      <article className="mx-auto w-full max-w-lg pb-16">
        <ReportTopBar onShare={handleShare} canShare={canShare} />
        <SectionSkeleton variant="hero" />
        {postSelfieModals}
      </article>
    );
  }

  if (isError || !report) {
    return (
      <article className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center gap-space-4 pb-16">
        <ReportTopBar onShare={handleShare} canShare={canShare} />
        <EmptyState
          icon={WarningCircle}
          tone="warning"
          title="Não foi possível abrir o relatório"
          description="A ligação falhou a meio. Os teus dados estão a salvo, é só tentar outra vez."
          action={
            <div className="flex flex-col items-center gap-space-3">
              <Button type="button" onClick={() => refetch()}>
                <ArrowClockwise className="mr-1.5 h-4 w-4" />
                Tentar novamente
              </Button>
              <Link to={paths.selfies} className="text-type-body-sm font-semibold text-sage-on-tint">
                {pt.snapshots.report.back}
              </Link>
            </div>
          }
        />
        {postSelfieModals}
      </article>
    );
  }

  if (isReportPending(report.status)) {
    return (
      <article className="mx-auto flex w-full max-w-lg flex-col gap-space-5 pb-16">
        <ReportTopBar onShare={handleShare} canShare={canShare} />
        <SelfieReportGenerating
          previousPeriodLabel={report.previous_selfie?.period?.label}
          slowGeneration={slowGeneration}
          onRefetch={() => refetch()}
          isFetching={isFetching}
        />
        {postSelfieModals}
      </article>
    );
  }

  const r = report;
  const aiNarrative = hasAiNarrative(r);
  const verdictTone = r.consultant_verdict?.tone || 'mixed';
  const nextPeriodLabel = r.selfie?.period?.label;
  const periodLabel = r.selfie?.period?.label || '';
  const generatedOn = r.generated_at
    ? new Date(r.generated_at).toLocaleDateString('pt-PT', { day: 'numeric', month: 'short' })
    : null;
  const selfieNumber = r.selfie?.number;

  return (
    <article className="mx-auto w-full max-w-lg pb-16 print:max-w-none">
      <ReportTopBar onShare={handleShare} canShare={canShare} />

      <div className="mb-space-5 flex flex-col gap-0.5">
        <h1 className="text-type-title-page font-semibold tracking-tight text-foreground">
          {periodLabel}
        </h1>
        {generatedOn || selfieNumber != null ? (
          <p className="text-type-body-sm text-muted-foreground">
            {generatedOn ? `Gerado a ${generatedOn}` : null}
            {generatedOn && selfieNumber != null ? ' · ' : null}
            {selfieNumber != null ? `a partir da tua ${selfieNumber}.ª selfie financeira` : null}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-space-5">
        {r.consultant_verdict?.headline ? (
          <SelfieReportVerdictBubble verdict={r.consultant_verdict} report={r} />
        ) : report.error_message && !aiNarrative ? (
          <ReportMessage
            tone="warning"
            icon={Sparkle}
            title="Não foi possível gerar a avaliação automática"
          >
            Os números do teu ciclo estão todos aqui em baixo, só a leitura do consultor é que
            falhou desta vez. Podes tentar de novo mais tarde.
          </ReportMessage>
        ) : null}

        <SelfieReportHighlights assessment={r.assessment} />

        <SelfieReportNextSteps report={r} nextPeriodLabel={nextPeriodLabel} />

        <SelfieReportDetailsAccordion report={r} verdictTone={verdictTone} />
      </div>

      {patrimonioCta}

      <footer className="mt-space-10 text-center font-mono text-xs text-muted-foreground print:mt-16">
        {pt.snapshots.report.footer}
      </footer>

      {postSelfieModals}
    </article>
  );
}

export default SelfieReport;
